import { Types } from './header';
import { buildPeriodIndex } from '../../../algorithms/utils';

/**
 * @function labelsFor
 * @description The `{periodKey: label}` map for a confirmed image list, as `buildPeriodIndex`
 * derives it. Kept next to the image list so the labels can never fall out of step with it: they
 * used to be written only when the classification step's elevation was confirmed, so stepping back
 * and changing a period left every tab, the result page and the exported GeoJSON naming the periods
 * by the previous selection.
 * @param {Array<Array<Object>>} imageList - One list of image infos per confirmed period.
 * @memberof module:Euscoast
 * @returns {Object} The `{periodKey: label}` map, empty when there is no period.
 */
const labelsFor = (imageList) => buildPeriodIndex(imageList || []).labels;

const INITIAL_STATE = {
  studyData: [],
  imageList: [],
  classLandCoversVectors: {},
  classLandCoverPolygons: {},
  waterLandCoverVectors: [],
  transectsCoords: [],
  transectsResults: [],
  years: [],
  dates: [],
  missions: [],
  periodLabels: {},
};

/**
 * @function reducer
 * @description Redux reducer for the EUSCoast duck. Handles setting the selected years/dates/
 * missions, the study data, per-year vectorized land-cover/water geometries (and their resets),
 * the individual land-cover polygons kept for export,
 * and the generated transect coordinates/results.
 * @param {Object} [state=INITIAL_STATE] - Current EUSCoast state.
 * @param {Object} action - Dispatched Redux action.
 * @param {String} action.type - Action type, from `Types`.
 * @param {Object} action.payload - Action payload.
 * **Called by:
 * @see {Redux store} - registered as the "euscoast" reducer.
 * @memberof module:Euscoast
 * @returns {Object} The next EUSCoast state.
 */
export const reducer = (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    case Types.SET_YEARS: {
      const { years } = payload;
      return {
        ...state,
        years,
      };
    }
    case Types.SET_DATES: {
      const { dates } = payload;
      return {
        ...state,
        dates,
      };
    }
    case Types.SET_MISSIONS: {
      const { missions } = payload;
      return {
        ...state,
        missions,
      };
    }
    case Types.SET_PERIOD_LABELS: {
      const { periodLabels } = payload;
      return {
        ...state,
        periodLabels,
      };
    }
    case Types.STUDY_DATA: {
      const { studyData } = payload;
      return {
        ...state,
        studyData,
      };
    }
    case Types.CLASS_LAND_COVERS: {
      const { classLandCovers, year } = payload;
      return {
        ...state,
        classLandCoversVectors: { ...state.classLandCoversVectors, [year]: classLandCovers },
      };
    }
    case Types.CLASS_LAND_COVER_POLYGONS: {
      const { polygons, year } = payload;
      return {
        ...state,
        classLandCoverPolygons: { ...state.classLandCoverPolygons, [year]: polygons },
      };
    }
    case Types.RESET_CLASS_LAND_COVERS: {
      return {
        ...state,
        classLandCoversVectors: {},
        classLandCoverPolygons: {},
      };
    }
    case Types.WATER_LAND_COVER: {
      const { waterLandCover } = payload;
      return {
        ...state,
        waterLandCoverVectors: [...state.waterLandCoverVectors, waterLandCover],
      };
    }
    case Types.RESET_WATER_LAND_COVER: {
      return {
        ...state,
        waterLandCoverVectors: [],
      };
    }
    case Types.TRANSECTS_COORDS: {
      const { transectsCoords } = payload;
      return {
        ...state,
        transectsCoords,
      };
    }
    case Types.TRANSECTS_RESULTS: {
      const { transectsResults } = payload;
      return {
        ...state,
        transectsResults,
      };
    }
    case Types.ADD_IMAGE_LIST: {
      const { imageList } = payload;
      const newList = state?.imageList ? [...state?.imageList, imageList] : [imageList];
      return {
        ...state,
        imageList: newList,
        periodLabels: labelsFor(newList),
      };
    }
    case Types.SET_IMAGE_LIST_AT: {
      const { index, imageList } = payload;
      const newList = [...(state.imageList || [])];
      newList[index] = imageList;
      newList.length = index + 1;
      return {
        ...state,
        imageList: newList,
        periodLabels: labelsFor(newList),
      };
    }
    case Types.TRUNCATE_IMAGE_LIST: {
      const { index } = payload;
      const newList = (state.imageList || []).slice(0, index);
      return {
        ...state,
        imageList: newList,
        periodLabels: labelsFor(newList),
      };
    }
    case Types.REMOVE_LAST_IMAGE_LIST: {
      if (!state.imageList?.length) return state;
      const newList = state.imageList.slice(0, -1);
      return {
        ...state,
        imageList: newList,
        periodLabels: labelsFor(newList),
      };
    }
    case Types.RESET_IMAGE_LIST: {
      return {
        ...state,
        imageList: [],
        periodLabels: {},
      };
    }
    case Types.RESET: {
      return INITIAL_STATE;
    }
    default: {
      return state;
    }
  }
};

export default reducer;
